import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { authSetup } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck, UserCircle, HardHat } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Administrador", desc: "Acceso total (incluye montos y contrato).", Icon: ShieldCheck, color: "text-orange-500" },
  operativo: { label: "Operativo", desc: "Todo, excepto montos y contrato.", Icon: UserCircle, color: "text-blue-500" },
  campo: { label: "Campo", desc: "Solo Acta de Hitos y Fotos.", Icon: HardHat, color: "text-emerald-500" },
};

export default function Login() {
  const { setupComplete, loading, login, refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("admin");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Setup state
  const [setupAdminPin, setSetupAdminPin] = useState("");
  const [setupOpPin, setSetupOpPin] = useState("");
  const [setupCampoPin, setSetupCampoPin] = useState("");

  useEffect(() => {
    if (!loading && setupComplete === false) {
      // stay on this page (setup mode)
    }
  }, [loading, setupComplete]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) {
      toast.error("PIN inválido (4-6 dígitos numéricos)");
      return;
    }
    setSubmitting(true);
    try {
      await login(role, pin);
      toast.success("Sesión iniciada");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "PIN incorrecto");
    } finally {
      setSubmitting(false);
    }
  };

  const onSetup = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(setupAdminPin)) {
      toast.error("PIN de admin obligatorio (4-6 dígitos)");
      return;
    }
    if (setupOpPin && !/^\d{4,6}$/.test(setupOpPin)) { toast.error("PIN operativo inválido"); return; }
    if (setupCampoPin && !/^\d{4,6}$/.test(setupCampoPin)) { toast.error("PIN campo inválido"); return; }
    setSubmitting(true);
    try {
      await authSetup({
        admin_pin: setupAdminPin,
        operativo_pin: setupOpPin || null,
        campo_pin: setupCampoPin || null,
      });
      await refresh();
      toast.success("Configuración inicial completa. Sesión de admin abierta.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en configuración inicial");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono text-sm">Cargando…</div>;
  }

  // Setup mode (first run)
  if (setupComplete === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="setup-page">
        <div className="w-full max-w-md bg-white border border-slate-200 p-8">
          <div className="coord-label mb-2">// CONFIGURACIÓN INICIAL</div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 mb-1">Crear PINs de acceso</h1>
          <p className="text-sm text-slate-600 mb-6">Define al menos el PIN del Administrador. Los otros son opcionales y puedes crearlos después desde tu Perfil.</p>
          <form onSubmit={onSetup} className="space-y-4">
            <div>
              <Label className="coord-label">PIN Administrador (obligatorio)</Label>
              <Input
                data-testid="setup-admin-pin"
                type="password" inputMode="numeric" autoComplete="off"
                value={setupAdminPin}
                onChange={(e) => setSetupAdminPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="4 a 6 dígitos"
                className="mt-1.5 rounded-sm font-mono text-lg tracking-widest"
              />
            </div>
            <div>
              <Label className="coord-label">PIN Operativo (opcional)</Label>
              <Input
                data-testid="setup-op-pin"
                type="password" inputMode="numeric" autoComplete="off"
                value={setupOpPin}
                onChange={(e) => setSetupOpPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="4 a 6 dígitos"
                className="mt-1.5 rounded-sm font-mono text-lg tracking-widest"
              />
            </div>
            <div>
              <Label className="coord-label">PIN Campo (opcional)</Label>
              <Input
                data-testid="setup-campo-pin"
                type="password" inputMode="numeric" autoComplete="off"
                value={setupCampoPin}
                onChange={(e) => setSetupCampoPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="4 a 6 dígitos"
                className="mt-1.5 rounded-sm font-mono text-lg tracking-widest"
              />
            </div>
            <Button
              type="submit" data-testid="setup-submit"
              disabled={submitting}
              className="w-full rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
            >
              {submitting ? "Guardando…" : "Crear y entrar como Administrador"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Login mode
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md bg-white border border-slate-200 p-8">
        <div className="flex items-center gap-2 mb-2">
          <Lock weight="duotone" size={20} color="#f97316" />
          <div className="coord-label">// ACCESO</div>
        </div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 mb-1">Deslinde Pro</h1>
        <p className="text-sm text-slate-600 mb-6">Selecciona tu acceso y digita tu PIN.</p>

        <div className="grid grid-cols-3 gap-2 mb-5">
          {Object.entries(ROLE_META).map(([key, m]) => {
            const Icon = m.Icon;
            const active = role === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`role-${key}`}
                onClick={() => setRole(key)}
                className={`p-3 border rounded-sm flex flex-col items-center gap-1 transition ${active ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <Icon weight="duotone" size={22} className={m.color} />
                <div className="text-xs font-bold text-slate-900">{m.label}</div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mb-4">{ROLE_META[role].desc}</p>

        <form onSubmit={onLogin} className="space-y-4">
          <div>
            <Label className="coord-label">PIN</Label>
            <Input
              data-testid="pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••"
              className="mt-1.5 rounded-sm font-mono text-2xl tracking-widest text-center"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="w-full rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
          >
            {submitting ? "Validando…" : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
